import { useState, useMemo, useCallback } from 'react';

import type { SortingState, PaginationState, RowSelectionState } from '@hs/ui';
import { AlertDialog, Button, Dropdown, Label, toast } from '@heroui/react';
import {
  IconPlus as Plus,
  IconDotsVertical as MoreVertical,
  IconTrash as Trash2,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import { useAppSelector } from '@/stores/hooks';
import {
  PageDataGrid,
  PageDataGridActionColumn,
  PageDataGridActionToolbar,
} from '@/components/layouts/page/PageDataGrid';
import { AppEditorModal } from './AppEditorModal';
import { RacDialogTriggerAnchor } from '@/components/RacDialogTriggerAnchor';

type App = {
  id: string;
  name: string;
  code?: string | null;
  description?: string | null;
  type?: string | null;
  status?: string | null;
  isVisible?: boolean | null;
  isBuiltin?: boolean | null;
  order?: number | null;
  createdAt: string;
};

const APPS_LIST_SORT_FIELDS = new Set(['name', 'order', 'type', 'status']);

function AppsActionToolbar({
  hasSelection,
  onCreate,
  onDelete,
}: {
  hasSelection: boolean;
  onCreate: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {hasSelection ? (
        <Button variant="danger" onPress={onDelete}>
          <Trash2 className="size-4" stroke={1.5} />
          删除
        </Button>
      ) : null}
      <Button onPress={onCreate}>
        <Plus className="size-4" stroke={1.5} />
        创建
      </Button>
    </div>
  );
}

export function AppListPanel() {
  const [searchKeyword, setSearchKeyword] = useState('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [enumFilters, setEnumFilters] = useState<Record<string, string>>({});

  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [editingApp, setEditingApp] = useState<App | null>(null);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [batchDeleteModalOpen, setBatchDeleteModalOpen] = useState(false);
  const [appToDelete, setAppToDelete] = useState<App | null>(null);

  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const isHydrated = useAppSelector((state) => state.auth.isHydrated);
  const utils = trpc.useUtils();

  const sortItem = sorting[0];
  const sortBy =
    sortItem && APPS_LIST_SORT_FIELDS.has(sortItem.id)
      ? (sortItem.id as 'name' | 'order' | 'type' | 'status')
      : undefined;

  const { data, isLoading } = trpc.bizApp.list.useQuery(
    {
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
      name: searchKeyword || undefined,
      ...(sortBy
        ? {
            sortBy,
            sortOrder: sortItem!.desc ? ('desc' as const) : ('asc' as const),
          }
        : {}),
      ...(enumFilters.type
        ? {
            type: enumFilters.type as 'SYSTEM_MANAGEMENT' | 'SUBSCRIPTION_MANAGEMENT' | 'WORKSPACE',
          }
        : {}),
      ...(enumFilters.status
        ? { status: enumFilters.status as 'ACTIVE' | 'DISABLED' | 'DEPRECATED' }
        : {}),
    },
    { enabled: isHydrated && isAuthenticated }
  );

  const apps = (data?.items ?? []) as App[];
  const total = data?.total ?? 0;
  const pageCount = useMemo(
    () => Math.ceil(total / (pagination.pageSize || 10)),
    [total, pagination.pageSize]
  );

  const deleteAppMutation = trpc.bizApp.delete.useMutation({
    onSuccess: () => {
      toast.success('应用删除成功');
      setDeleteModalOpen(false);
      setAppToDelete(null);
      void utils.bizApp.list.invalidate();
    },
    onError: (error) => {
      toast.danger('删除失败', { description: error.message });
    },
  });

  const deleteManyAppsMutation = trpc.bizApp.deleteBatch.useMutation({
    onSuccess: (_data, variables) => {
      toast.success(`成功删除 ${variables.ids.length} 个应用`);
      setRowSelection({});
      setBatchDeleteModalOpen(false);
      void utils.bizApp.list.invalidate();
    },
    onError: (error) => {
      toast.danger('批量删除失败', {
        description: error.message,
      });
    },
  });

  const handleCreate = () => {
    setEditingApp(null);
    setEditorModalOpen(true);
  };

  const handleEdit = (app: App) => {
    setEditingApp(app);
    setEditorModalOpen(true);
  };

  const handleEditorSuccess = () => {
    setEditorModalOpen(false);
    setEditingApp(null);
    void utils.bizApp.list.invalidate();
  };

  const handleEditorCancel = useCallback(() => {
    setEditorModalOpen(false);
    setEditingApp(null);
  }, []);

  const handleDelete = (app: App) => {
    setAppToDelete(app);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!appToDelete) return;
    try {
      await deleteAppMutation.mutateAsync({ id: appToDelete.id });
    } catch {
      /* onError */
    }
  };

  const selectedIds = useMemo(
    () => Object.keys(rowSelection).filter((key) => rowSelection[key]),
    [rowSelection]
  );

  const handleBatchDeleteOpen = () => {
    if (selectedIds.length === 0) {
      toast.warning('请先选择要删除的应用');
      return;
    }
    setBatchDeleteModalOpen(true);
  };

  const confirmBatchDelete = async () => {
    if (selectedIds.length === 0) return;
    try {
      await deleteManyAppsMutation.mutateAsync({ ids: selectedIds });
    } catch {
      /* onError */
    }
  };

  const selectedCount = selectedIds.length;

  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col">
      {/* flex-1 只给表格，避免与 Modal 的 DialogTrigger 同列争高或层叠盖住工具栏 */}
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <PageDataGrid<App>
          className="flex min-h-0 min-w-0 flex-1 flex-col"
          source="app"
          data={apps}
          recordCount={total}
          pageCount={pageCount}
          enableSelectColumn
          isLoading={isLoading}
          emptyMessage="暂无数据"
          getRowId={(row) => row.id}
          sorting={sorting}
          onSortingChange={setSorting}
          pagination={pagination}
          onPaginationChange={setPagination}
          rowSelection={rowSelection}
          onRowSelectionChange={setRowSelection}
          searchKeyword={searchKeyword}
          onSearchKeywordChange={setSearchKeyword}
          enumFilters={enumFilters}
          onEnumFiltersChange={setEnumFilters}
          metaQueryOptions={{ enabled: isHydrated && isAuthenticated }}
          paginationBar={{}}
        >
          <PageDataGridActionToolbar>
            <AppsActionToolbar
              hasSelection={selectedCount > 0}
              onCreate={handleCreate}
              onDelete={handleBatchDeleteOpen}
            />
          </PageDataGridActionToolbar>
          <PageDataGridActionColumn<App>>
            {({ row }) => {
              const app = row.original;
              return (
                <div className="flex justify-center">
                  <Dropdown>
                    <Button isIconOnly aria-label="操作" size="sm" variant="ghost">
                      <MoreVertical className="size-4" stroke={1.5} />
                    </Button>
                    <Dropdown.Popover className="min-w-[140px]" placement="bottom end">
                      <Dropdown.Menu>
                        <Dropdown.Item
                          id="edit"
                          textValue="编辑"
                          onAction={() => handleEdit(app)}
                        >
                          <Label>编辑</Label>
                        </Dropdown.Item>
                        <Dropdown.Item
                          id="delete"
                          textValue="删除"
                          variant="danger"
                          onAction={() => handleDelete(app)}
                        >
                          <Trash2 className="size-4 shrink-0" stroke={1.5} />
                          <Label>删除</Label>
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown.Popover>
                  </Dropdown>
                </div>
              );
            }}
          </PageDataGridActionColumn>
        </PageDataGrid>
      </div>

      <AppEditorModal
        open={editorModalOpen}
        app={editingApp}
        onCancel={handleEditorCancel}
        onSuccess={handleEditorSuccess}
      />

      <AlertDialog
        isOpen={deleteModalOpen}
        onOpenChange={(open) => {
          setDeleteModalOpen(open);
          if (!open) {
            setAppToDelete(null);
          }
        }}
      >
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除应用「{appToDelete?.name}」吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  isDisabled={deleteAppMutation.isPending}
                  onPress={() => {
                    setDeleteModalOpen(false);
                    setAppToDelete(null);
                  }}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteAppMutation.isPending}
                  onPress={() => void confirmDelete()}
                >
                  {deleteAppMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>

      <AlertDialog
        isOpen={batchDeleteModalOpen}
        onOpenChange={(open) => {
          setBatchDeleteModalOpen(open);
        }}
      >
        <RacDialogTriggerAnchor />
        <AlertDialog.Backdrop>
          <AlertDialog.Container placement="center">
            <AlertDialog.Dialog>
              <AlertDialog.Header>
                <AlertDialog.Heading>确认批量删除</AlertDialog.Heading>
              </AlertDialog.Header>
              <AlertDialog.Body>
                确定要删除选中的 {selectedIds.length} 个应用吗？此操作不可恢复。
              </AlertDialog.Body>
              <AlertDialog.Footer>
                <Button
                  variant="secondary"
                  isDisabled={deleteManyAppsMutation.isPending}
                  onPress={() => setBatchDeleteModalOpen(false)}
                >
                  取消
                </Button>
                <Button
                  variant="danger"
                  isDisabled={deleteManyAppsMutation.isPending}
                  onPress={() => void confirmBatchDelete()}
                >
                  {deleteManyAppsMutation.isPending ? '删除中…' : '确认删除'}
                </Button>
              </AlertDialog.Footer>
            </AlertDialog.Dialog>
          </AlertDialog.Container>
        </AlertDialog.Backdrop>
      </AlertDialog>
    </div>
  );
}
